import {
  HttpContextToken,
  HttpErrorResponse,
  HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../../services/auth.service';
import { API_ERROR_CODES } from '../../shared/constants/api-error-codes';

/** Prevents infinite retry when refresh still returns TOKEN_STALE_PERMISSIONS. */
const STALE_PERMISSION_RETRY = new HttpContextToken(() => false);

const PUBLIC_API_PREFIXES = ['/api/v1/auth/', '/api/v1/health', '/api/v1/public/'];
// GET /api/v1/auth/invite/:token is public (no Bearer)

function toPathname(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}

function isApiV1Request(url: string): boolean {
  return toPathname(url).includes('/api/v1/');
}

function isPublicApiRequest(url: string): boolean {
  const path = toPathname(url);
  return PUBLIC_API_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function isTenantApiRequest(url: string): boolean {
  const path = toPathname(url);
  return path.includes('/api/v1/tenant/');
}

/** Paths that require tenant context (x-tenant-id) outside the /tenant namespace. */
function isPlatformPathRequiringTenantContext(url: string): boolean {
  const path = toPathname(url);
  return (
    path === '/api/v1/platform/external/shopify/auth-url' ||
    path.startsWith('/api/v1/custom-apps-shopify/')
  );
}

/** Tenant-scoped support APIs under `platform/support/*` (widget + tickets). */
function isPlatformSupportTenantRequest(url: string): boolean {
  const path = toPathname(url);
  return path.startsWith('/api/v1/platform/support/');
}

/** POST tenant/select is under /auth/* (skipped as "public") but must send Bearer for session refresh. */
function isTenantSelectPostRequest(url: string, method: string): boolean {
  const path = toPathname(url);
  const m = (method || '').toUpperCase();
  return m === 'POST' && path === '/api/v1/auth/tenant/select';
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const token = authService.getAuthToken();
  const isApiRequest = isApiV1Request(request.url);
  const isPublicRequest = isPublicApiRequest(request.url);
  const isTenantRequest = isTenantApiRequest(request.url);
  const hasValidToken = Boolean(token && authService.isTokenValid(token));
  const shouldAttachBearer =
    isApiRequest &&
    (!isPublicRequest || isTenantSelectPostRequest(request.url, request.method)) &&
    hasValidToken;

  let clonedRequest = request.clone({
    headers: request.headers.set('apiKey', environment.apiKey).set('accept', 'application/json'),
  });

  if (shouldAttachBearer && token) {
    clonedRequest = clonedRequest.clone({
      headers: clonedRequest.headers.set('Authorization', `Bearer ${token}`),
    });
  }

  // Tenant routes (and platform paths that need tenant context): send x-tenant-id.
  const needsTenantId =
    (isTenantRequest ||
      isPlatformPathRequiringTenantContext(request.url) ||
      isPlatformSupportTenantRequest(request.url)) &&
    shouldAttachBearer;
  if (isApiRequest && !isPublicRequest && needsTenantId) {
    const tenantId =
      authService.getTenantIdForRoutingHeader() ??
      authService.getTenantIdFromToken() ??
      authService.getTenantId();
    if (tenantId) {
      clonedRequest = clonedRequest.clone({
        headers: clonedRequest.headers.set('x-tenant-id', tenantId),
      });
    }
  }

  return next(clonedRequest).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401 && isApiRequest && !isPublicRequest) {
        authService.handleUnauthorized();
      }

      // Tenant inactive is a hard stop: logout immediately, show blocking modal, and never retry/refresh.
      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        isApiRequest &&
        !isPublicRequest
      ) {
        const body = error.error;
        const code =
          typeof body === 'object' &&
          body !== null &&
          'code' in body &&
          typeof (body as { code?: unknown }).code === 'string'
            ? (body as { code: string }).code
            : '';
        if (code === API_ERROR_CODES.TENANT_INACTIVE) {
          // Tenant inactive handling is tenant-user only (platform/admin should never be interrupted).
          if (authService.getUserTypeFromToken() === 'tenant_user') {
            authService.handleTenantInactiveFromApi();
          }
          return throwError(() => error);
        }
        if (code === API_ERROR_CODES.USER_INACTIVE) {
          if (authService.getUserTypeFromToken() === 'tenant_user') {
            const tenantName =
              typeof (body as any)?.tenantName === 'string'
                ? String((body as any).tenantName).trim()
                : null;
            const tenantEmail =
              typeof (body as any)?.tenantEmail === 'string'
                ? String((body as any).tenantEmail).trim()
                : null;
            authService.handleUserInactiveFromApi({ tenantName, tenantEmail });
          }
          return throwError(() => error);
        }
      }

      if (
        error instanceof HttpErrorResponse &&
        error.status === 403 &&
        isApiRequest &&
        !isPublicRequest &&
        !clonedRequest.context.get(STALE_PERMISSION_RETRY)
      ) {
        const body = error.error;
        const code =
          typeof body === 'object' &&
          body !== null &&
          'code' in body &&
          typeof (body as { code?: unknown }).code === 'string'
            ? (body as { code: string }).code
            : '';
        if (code === 'TOKEN_STALE_PERMISSIONS' && authService.getUserTypeFromToken() === 'tenant_user') {
          return from(authService.refreshTenantSession()).pipe(
            switchMap(() =>
              next(
                clonedRequest.clone({
                  context: clonedRequest.context.set(STALE_PERMISSION_RETRY, true),
                }),
              ),
            ),
            catchError((e) => throwError(() => e)),
          );
        }
      }

      return throwError(() => error);
    }),
  );
};
